import React, { useState, useEffect, useMemo } from "react";

import {
    Card,
    Table,
    Button,
    Input,
    Space,
    Typography,
    Statistic,
    Row,
    Col,
    Tag,
    Tooltip,
    Empty,
    Modal,
    Spin
} from "antd";

import {
    SearchOutlined,
    DeleteOutlined,
    AppstoreOutlined,
    ExclamationCircleFilled
} from "@ant-design/icons";

import Swal from "sweetalert2";

import api from "../../api/api";

const { Title, Text } = Typography;
const { confirm } = Modal;

const ClassViewList = () => {

    const token = localStorage.getItem("access");

    const [classes, setClasses] = useState([]);

    const [loading, setLoading] = useState(true);

    const [searchText, setSearchText] = useState("");



    // -----------------------------------
    // Fetch Classes
    // -----------------------------------

    const fetchClasses = async () => {

        if (!token) return;

        setLoading(true);

        try {

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            const response = await api.get(
                "api/users/grade-classes/",
                config
            );

            setClasses(response.data);

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Failed",
                text: "Unable to load classes."
            });

        } finally {

            setLoading(false);

        }

    };



    useEffect(() => {

        fetchClasses();

    }, []);



    // -----------------------------------
    // Delete Class
    // -----------------------------------

    const handleDelete = (id) => {

        confirm({

            title: "Delete Class",

            icon: <ExclamationCircleFilled />,

            content:
                "Are you sure you want to delete this class? This action cannot be undone.",

            okText: "Delete",

            okType: "danger",

            cancelText: "Cancel",

            centered: true,

            async onOk() {

                try {

                    const config = {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    };

                    await api.delete(
                        `api/users/grade-classes/${id}/`,
                        config
                    );

                    setClasses((previous) =>
                        previous.filter(
                            (item) => item.id !== id
                        )
                    );

                    Swal.fire({

                        icon: "success",

                        title: "Deleted",

                        text:
                            "Class deleted successfully.",

                        timer: 1800,

                        showConfirmButton: false

                    });

                } catch (error) {

                    Swal.fire({

                        icon: "error",

                        title: "Delete Failed",

                        text:
                            "Unable to delete class."

                    });

                }

            }

        });

    };



    // -----------------------------------
    // Search Filter
    // -----------------------------------

    const filteredClasses = useMemo(() => {

        return classes.filter((item) => {

            const keyword = searchText.toLowerCase();

            return (

                item.name
                    ?.toLowerCase()
                    .includes(keyword)

                ||

                item.division?.name
                    ?.toLowerCase()
                    .includes(keyword)

                ||

                String(item.id).includes(keyword)

            );

        });

    }, [classes, searchText]);
    // -----------------------------------
    // Table Columns
    // -----------------------------------

    const columns = [

        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            align: "center",
            sorter: (a, b) => a.id - b.id,

            render: (id) => (

                <Tag
                    color="geekblue"
                    style={{
                        fontWeight: 600,
                        minWidth: 40,
                        textAlign: "center"
                    }}
                >
                    {id}
                </Tag>

            )

        },

        {
            title: "Class Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),

            render: (name) => (

                <Text strong>
                    {name}
                </Text>

            )

        },

        {
            title: "Division",
            key: "division",

            sorter: (a, b) =>
                (a.division?.name || "").localeCompare(
                    b.division?.name || ""
                ),

            render: (_, record) => (

                record.division ? (

                    <Tag
                        color="green"
                        style={{
                            borderRadius: 20,
                            padding: "2px 12px",
                            fontWeight: 500
                        }}
                    >
                        {record.division.name}
                    </Tag>

                ) : (

                    <Tag color="default">
                        No Division
                    </Tag>

                )

            )

        },

        {
            title: "Actions",
            key: "actions",
            align: "center",
            width: 150,

            render: (_, record) => (

                <Tooltip title="Delete Class">

                    <Button
                        danger
                        type="primary"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    >
                        Delete
                    </Button>

                </Tooltip>

            )

        }

    ];



    // -----------------------------------
    // Pagination
    // -----------------------------------

    const paginationConfig = {

        pageSize: 8,

        showSizeChanger: true,

        pageSizeOptions: ["5", "8", "10", "20"],

        showQuickJumper: true,

        showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} Classes`

    };



    // -----------------------------------
    // Search Box
    // -----------------------------------

    const SearchBar = () => (

<Input
    allowClear
    size="large"
    prefix={<SearchOutlined />}
    placeholder="Search class..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
        width: 350,
        borderRadius: 8
    }}
/>

    );

    return (

<div
    style={{
        padding: 24,
        background: "#f5f6fa"
    }}
>

            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0,0,0,.08)"
                }}
bodyStyle={{
    padding:18
}}
          
            >

                {/* Statistics */}

         


                {/* Search */}

                <Row
                    justify="space-between"
                    align="middle"
                    gutter={[16, 16]}
                    style={{
                        marginBottom: 20
                    }}
                >

                    <Col xs={24} md={10}>

                        <SearchBar />

                    </Col>

                </Row>

                <div
    style={{
        marginBottom: 20
    }}
>

    <Tag
        style={{
            background: "#ff8800",
            color: "#000000",
            border: "none",
            borderRadius: 20,
            padding: "6px 16px",
            fontSize: 14,
            fontWeight: 600
        }}
    >
        Total Classes : {filteredClasses.length}
    </Tag>

</div>


                {/* Table */}

                <Spin spinning={loading} size="large">

                    <Table

                        rowKey="id"

                        columns={columns}

                        dataSource={filteredClasses}

                        pagination={paginationConfig}

                        bordered

                        scroll={{
                            x: 700
                        }}

                        locale={{

                            emptyText: (

                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No Classes Available"
                                />

                            )

                        }}

                        rowClassName={(_, index) =>
                            index % 2 === 0
                                ? "table-row-light"
                                : "table-row-white"
                        }

                    />

                </Spin>

            </Card>

            {/* Component Styles */}

            <style>

                {`

                    .ant-table-thead > tr > th{

                        background: #C99700 !important;

                        color:#fff !important;

                        font-weight:600 !important;

                        text-align:center;

                    }

                    .ant-table-tbody > tr.table-row-light{

                        background:#fafafa;

                    }

                    .ant-table-tbody > tr.table-row-white{

                        background:#ffffff;

                    }

                    .ant-table-tbody > tr:hover > td{

                        background:#eeeeee !important;

                    }

                    .ant-card{

                        border-radius:12px;

                    }

                    @media(max-width:768px){

                        .ant-statistic{

                            text-align:center;

                        }

                    }

                `}

            </style>

        </div>

    );

};

export default ClassViewList;